class TempoFiberQuiz extends HTMLElement {
  constructor() {
    super();
    this.allQuestions = [];
    this.questions = [];
    this.screens = {};
    this.state = {
      started: false,
      currentQuestion: 0,
      answers: [],
      unlocked: false,
      difficulty: 'beginner',
      pendingLead: false,
    };
    this.storageKey = 'tempo-quiz:fiber:v3';
    this.leadReturnState = null;
  }

  connectedCallback() {
    this.allQuestions = Array.from(this.querySelectorAll('[data-quiz-question]'));
    this.screens = Object.fromEntries(
      Array.from(this.querySelectorAll('[data-quiz-screen]')).map((screen) => [screen.dataset.quizScreen, screen])
    );

    this.restoreState();
    this.configureDifficulty(this.state.difficulty);
    this.completeNativeLeadSubmission();
    this.repairPageNavigation();
    this.watchPageNavigation();
    this.bindEvents();
    this.applySavedAnswers();

    if (this.state.unlocked) {
      this.showResults();
    } else if (this.leadReturnState) {
      this.showGate(false);
      this.setEmailLoading(this.querySelector('[data-email-submit]'), false);
      if (this.leadReturnState === 'invalid') {
        const message = this.querySelector('[data-email-message]');
        if (message) message.textContent = 'Complete the email form to unlock your detailed results.';
      }
    } else if (this.state.started) {
      this.showQuestion(this.state.currentQuestion, false);
    } else {
      this.showScreen('intro');
    }
  }

  bindEvents() {
    this.querySelector('[data-quiz-start]')?.addEventListener('click', this.handleStart);
    this.querySelector('[data-quiz-back]')?.addEventListener('click', this.handleBack);
    this.querySelector('[data-review-before-unlock]')?.addEventListener('click', this.handleReviewBeforeUnlock);
    this.querySelector('[data-copy-discount]')?.addEventListener('click', this.handleCopyDiscount);
    this.querySelector('[data-toggle-review]')?.addEventListener('click', this.handleToggleReview);
    this.querySelector('.fiber-quiz__email-form')?.addEventListener('submit', this.handleEmailSubmit);
    this.querySelector('[data-add-tempo]')?.addEventListener('click', this.handleAddTempo);

    this.querySelectorAll('[data-difficulty-option]').forEach((option) => {
      option.addEventListener('change', this.handleDifficultyChange);
    });

    this.querySelectorAll('[data-purchase-option]').forEach((option) => {
      option.addEventListener('change', this.handlePurchaseOptionChange);
    });

    this.allQuestions.forEach((question) => {
      question.querySelectorAll('[data-answer-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const checkButton = question.querySelector('[data-check-answer]');
          if (checkButton) checkButton.disabled = false;
        });
      });
      question.querySelector('[data-check-answer]')?.addEventListener('click', () => this.gradeQuestion(question));
      question.querySelector('[data-next-question]')?.addEventListener('click', () => this.advanceFrom(question));
    });
  }

  repairPageNavigation() {
    const destinationByAnchor = {
      '#product-information-bg': '/',
      '#how-it-works': '/#how-it-works',
      '#supplement=true': '/#supplement=true',
      '#index-faq-section': '/#index-faq-section',
    };
    const destinationByLabel = {
      'save up to 40%': '/',
      'how it works': '/#how-it-works',
      ingredients: '/#supplement=true',
      faqs: '/#index-faq-section',
      contact: '/pages/contact',
      'privacy policy': '/policies/privacy-policy',
      'terms and conditions': '/policies/terms-of-service',
    };

    document.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      const label = (link.textContent || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const destination = destinationByAnchor[href] || destinationByLabel[label];
      if (destination && href !== destination) link.setAttribute('href', destination);
    });
  }

  watchPageNavigation() {
    this.navigationObserver?.disconnect();
    this.navigationObserver = new MutationObserver(() => this.repairPageNavigation());
    this.navigationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['href'],
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    this.navigationObserver?.disconnect();
  }

  handleDifficultyChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.checked) return;

    this.resetQuestionUi();
    this.state = {
      started: false,
      currentQuestion: 0,
      answers: [],
      unlocked: false,
      difficulty: event.target.value,
      pendingLead: false,
    };
    this.configureDifficulty(event.target.value);
    const review = this.querySelector('[data-answer-review]');
    if (review) review.replaceChildren();
    this.saveState();
  };

  configureDifficulty(difficulty = 'beginner') {
    const ranks = { beginner: 0, medium: 1, hard: 2 };
    const safeDifficulty = Object.prototype.hasOwnProperty.call(ranks, difficulty) ? difficulty : 'beginner';
    const selectedRank = ranks[safeDifficulty];

    this.state.difficulty = safeDifficulty;
    this.questions = this.allQuestions.filter((question) => {
      const questionRank = ranks[question.dataset.questionDifficulty] ?? 0;
      return questionRank <= selectedRank;
    });

    this.allQuestions.forEach((question) => {
      question.hidden = true;
    });
    this.questions.forEach((question, index) => {
      question.dataset.questionIndex = String(index);
      const nextButton = question.querySelector('[data-next-question]');
      if (nextButton) nextButton.textContent = index === this.questions.length - 1 ? 'See my score' : 'Next question';
    });

    this.state.currentQuestion = Math.min(
      Math.max(Number(this.state.currentQuestion) || 0, 0),
      Math.max(this.questions.length - 1, 0)
    );
    this.state.answers = Array.isArray(this.state.answers)
      ? this.state.answers.slice(0, this.questions.length)
      : [];

    const minutes = safeDifficulty === 'hard' ? 6 : safeDifficulty === 'medium' ? 4 : 2;
    const meta = `${this.questions.length} QUESTIONS · ABOUT ${minutes} MINUTES`;
    this.querySelectorAll('[data-quiz-meta]').forEach((item) => {
      item.textContent = meta;
    });
    this.querySelectorAll('[data-difficulty-option]').forEach((option) => {
      if (option instanceof HTMLInputElement) option.checked = option.value === safeDifficulty;
    });
    this.querySelector('[data-quiz-progress]')?.setAttribute('aria-valuemax', String(this.questions.length));
    this.dataset.difficulty = safeDifficulty;
  }

  resetQuestionUi() {
    this.allQuestions.forEach((question) => {
      question.querySelectorAll('[data-answer-input]').forEach((input) => {
        input.disabled = false;
        input.checked = false;
        input.closest('[data-answer-card]')?.classList.remove('is-correct', 'is-incorrect');
      });
      const feedback = question.querySelector('[data-answer-feedback]');
      const checkButton = question.querySelector('[data-check-answer]');
      const nextButton = question.querySelector('[data-next-question]');
      if (feedback) feedback.hidden = true;
      if (checkButton) {
        checkButton.hidden = false;
        checkButton.disabled = true;
      }
      if (nextButton) nextButton.hidden = true;
    });
  }

  handleStart = () => {
    this.state.started = true;
    this.saveState();
    this.track('tempo_quiz_started', {
      quiz_type: this.dataset.quizType,
      difficulty: this.state.difficulty,
      total: this.questions.length,
    });
    this.showQuestion(this.state.currentQuestion || 0);
  };

  handleBack = () => {
    if (this.state.currentQuestion <= 0) {
      this.showScreen('intro');
      return;
    }

    this.showQuestion(this.state.currentQuestion - 1);
  };

  handleReviewBeforeUnlock = () => {
    this.showQuestion(Math.max(0, this.questions.length - 1));
  };

  gradeQuestion(question) {
    const index = Number(question.dataset.questionIndex);
    const selected = question.querySelector('[data-answer-input]:checked');
    if (!(selected instanceof HTMLInputElement)) return;

    const correctAnswer = question.dataset.correctAnswer;
    const isCorrect = selected.value === correctAnswer;
    this.state.answers[index] = {
      selected: selected.value,
      correct: isCorrect,
      graded: true,
    };

    this.renderGradedQuestion(question, this.state.answers[index]);
    this.updateProgress();
    this.saveState();
    this.track('tempo_quiz_answered', {
      quiz_type: this.dataset.quizType,
      difficulty: this.state.difficulty,
      question_number: Number(question.dataset.questionNumber) || index + 1,
      correct: isCorrect,
    });
  }

  renderGradedQuestion(question, answer) {
    const correctAnswer = question.dataset.correctAnswer;
    const feedback = question.querySelector('[data-answer-feedback]');
    const feedbackTitle = question.querySelector('[data-feedback-title]');
    const feedbackCopy = question.querySelector('[data-feedback-copy]');
    const checkButton = question.querySelector('[data-check-answer]');
    const nextButton = question.querySelector('[data-next-question]');

    question.querySelectorAll('[data-answer-input]').forEach((input) => {
      input.disabled = true;
      input.checked = input.value === answer.selected;
      const card = input.closest('[data-answer-card]');
      card?.classList.toggle('is-correct', input.value === correctAnswer);
      card?.classList.toggle('is-incorrect', input.value === answer.selected && !answer.correct);
    });

    if (feedback) {
      feedback.hidden = false;
      feedback.classList.toggle('is-correct', answer.correct);
      feedback.classList.toggle('is-incorrect', !answer.correct);
    }
    if (feedbackTitle) {
      feedbackTitle.textContent = 'Field note';
    }
    if (feedbackCopy) {
      feedbackCopy.textContent = answer.correct
        ? question.dataset.correctResponse || question.dataset.explanation || ''
        : question.dataset.incorrectResponse || question.dataset.explanation || '';
    }
    if (checkButton) checkButton.hidden = true;
    if (nextButton) nextButton.hidden = false;

    this.setStatus(answer.correct ? 'Correct answer.' : 'Answer reviewed. The correct choice is highlighted.');
  }

  advanceFrom(question) {
    const index = Number(question.dataset.questionIndex);
    if (index >= this.questions.length - 1) {
      this.showGate();
      return;
    }
    this.showQuestion(index + 1);
  }

  showQuestion(index, focusHeading = true) {
    const safeIndex = Math.min(Math.max(index, 0), this.questions.length - 1);
    this.state.started = true;
    this.state.currentQuestion = safeIndex;
    this.showScreen('questions');

    this.allQuestions.forEach((question) => {
      question.hidden = true;
    });
    this.questions.forEach((question, questionIndex) => {
      question.hidden = questionIndex !== safeIndex;
    });

    this.updateProgress();
    this.saveState();

    if (focusHeading) {
      window.requestAnimationFrame(() => {
        this.questions[safeIndex]?.querySelector('[data-question-heading]')?.focus({ preventScroll: true });
        this.scrollExperienceIntoView();
      });
    }
  }

  showGate(trackCompletion = true) {
    const result = this.getResult();
    const score = this.getScore();
    const total = this.questions.length;
    const percentage = total ? Math.round((score / total) * 100) : 0;

    this.querySelector('[data-score-number]').textContent = `${score}/${total}`;
    this.querySelector('[data-result-name]').textContent = result.name;
    this.querySelector('[data-result-headline]').textContent = result.headline;
    this.querySelector('[data-score-ring]')?.style.setProperty('--quiz-score', percentage);
    this.updateLeadMetadata(result);

    this.showScreen('gate');
    if (trackCompletion) {
      this.track('tempo_quiz_completed', {
        quiz_type: this.dataset.quizType,
        score,
        total,
        percentage,
        result: result.name,
        difficulty: this.state.difficulty,
      });
    }
    window.requestAnimationFrame(() => this.scrollExperienceIntoView());
  }

  showResults() {
    const result = this.getResult();
    const score = this.getScore();
    const total = this.questions.length;
    const firstMissedIndex = this.state.answers.findIndex((answer) => answer?.graded && !answer.correct);
    const firstMissedQuestion = firstMissedIndex >= 0 ? this.questions[firstMissedIndex] : null;

    this.querySelector('[data-final-score]').textContent = `${score}/${total}`;
    this.querySelector('[data-final-result]').textContent = result.name;
    this.querySelector('[data-final-headline]').textContent = `${result.headline} ${result.copy}`;
    this.querySelector('[data-result-strength]').textContent =
      score === total
        ? 'You handled every label, source and fiber fact in the quiz.'
        : `You answered ${score} of ${total} correctly and turned every miss into something useful.`;
    this.querySelector('[data-result-gap]').textContent = firstMissedQuestion
      ? firstMissedQuestion.dataset.explanation
      : 'Nothing caught you this time. Your next challenge is turning that knowledge into a routine.';

    this.buildAnswerReview();
    this.showScreen('results');
    window.requestAnimationFrame(() => this.scrollExperienceIntoView());
  }

  showScreen(name) {
    Object.entries(this.screens).forEach(([screenName, screen]) => {
      screen.hidden = screenName !== name;
    });
    this.dataset.activeScreen = name;
  }

  updateProgress() {
    const current = this.state.currentQuestion;
    const total = this.questions.length;
    const score = this.getScore();
    const progress = total ? ((current + 1) / total) * 100 : 0;
    const progressElement = this.querySelector('[data-quiz-progress]');

    this.querySelector('[data-quiz-progress-label]').textContent = `Question ${current + 1} of ${total}`;
    this.querySelector('[data-quiz-score-label]').textContent = `${score} correct`;
    this.querySelector('[data-quiz-progress-bar]')?.style.setProperty('width', `${progress}%`);
    progressElement?.setAttribute('aria-valuenow', String(current + 1));
    this.querySelector('[data-quiz-back]')?.classList.toggle('is-at-start', current === 0);
  }

  getScore() {
    return this.state.answers.filter((answer) => answer?.graded && answer.correct).length;
  }

  getResult() {
    const score = this.getScore();
    const total = this.questions.length;
    const percentage = total ? (score / total) * 100 : 0;
    const bands = Array.from(this.querySelectorAll('[data-result-band]'));
    const bandIndex = percentage >= 85 ? 3 : percentage >= 65 ? 2 : percentage >= 40 ? 1 : 0;
    const band = bands.find((item) => Number(item.dataset.bandIndex) === bandIndex) || bands[0];

    return {
      name: band?.dataset.name || 'Fiber Curious',
      headline: band?.dataset.headline || 'Your Fiber IQ is taking shape.',
      copy: band?.dataset.copy || '',
    };
  }

  updateLeadMetadata(result = this.getResult()) {
    const score = this.getScore();
    const resultSlug = result.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const tags = this.querySelector('[data-quiz-tags]');

    if (tags) {
      tags.value =
        `newsletter,tempo_quiz,quiz_fiber,quiz_difficulty_${this.state.difficulty},` +
        `quiz_result_${resultSlug},quiz_score_${score},quiz_total_${this.questions.length}`;
    }
  }

  handleEmailSubmit = (event) => {
    const form = event.currentTarget;
    const emailInput = form.querySelector('[data-quiz-email]');
    const consent = form.querySelector('[data-quiz-consent]');
    const submitButton = form.querySelector('[data-email-submit]');
    const message = form.querySelector('[data-email-message]');

    if (!(emailInput instanceof HTMLInputElement) || !(consent instanceof HTMLInputElement)) {
      event.preventDefault();
      return;
    }
    if (!emailInput.reportValidity() || !consent.reportValidity()) {
      event.preventDefault();
      return;
    }

    const result = this.getResult();
    const email = emailInput.value.trim();
    const score = this.getScore();
    const total = this.questions.length;
    this.updateLeadMetadata(result);
    this.state.unlocked = false;
    this.state.pendingLead = true;
    this.saveState();
    this.setEmailLoading(submitButton, true);
    if (message) message.textContent = 'Saving your results…';
    this.identifyKlaviyo(email, result);
    this.track('tempo_quiz_lead_submitted', {
      quiz_type: this.dataset.quizType,
      score,
      total,
      result: result.name,
      difficulty: this.state.difficulty,
      delivery_method: 'shopify_customer_form',
    });
  };

  completeNativeLeadSubmission() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('quiz_submitted') !== '1') return;

    const wasPending = this.state.pendingLead;
    const hasShopifyError = Boolean(this.querySelector('[data-shopify-form-error]')?.textContent.trim());
    const wasCaptured = wasPending && !hasShopifyError;

    this.state.unlocked = wasCaptured;
    this.state.pendingLead = false;
    this.leadReturnState = wasCaptured ? 'captured' : hasShopifyError ? 'error' : 'invalid';
    this.saveState();

    if (wasCaptured) {
      this.track('tempo_quiz_lead_captured', {
        quiz_type: this.dataset.quizType,
        score: this.getScore(),
        total: this.questions.length,
        difficulty: this.state.difficulty,
        delivery_method: 'shopify_customer_form',
      });
    }

    url.searchParams.delete('quiz_submitted');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  identifyKlaviyo(email, result) {
    const properties = {
      email,
      quiz_type: this.dataset.quizType,
      quiz_difficulty: this.state.difficulty,
      quiz_score: this.getScore(),
      quiz_total: this.questions.length,
      quiz_result: result.name,
    };

    try {
      if (typeof window.klaviyo?.identify === 'function') {
        window.klaviyo.identify(properties);
      } else if (Array.isArray(window._learnq)) {
        window._learnq.push(['identify', { $email: email, ...properties }]);
      }
    } catch (error) {
      // Identification is supplemental; Shopify and list subscription are the lead sources of record.
    }
  }

  setEmailLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
    const label = button.querySelector('span');
    if (label) label.textContent = loading ? 'Unlocking…' : 'Unlock my extra credit';
  }

  handleCopyDiscount = async () => {
    const code = this.dataset.discountCode;
    const label = this.querySelector('[data-copy-label]');
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = code;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }

    if (label) {
      label.textContent = 'Copied';
      window.setTimeout(() => {
        label.textContent = 'Copy code';
      }, 1800);
    }
  };

  handleToggleReview = () => {
    const review = this.querySelector('[data-answer-review]');
    const button = this.querySelector('[data-toggle-review]');
    if (!review || !button) return;

    const willOpen = review.hidden;
    review.hidden = !willOpen;
    button.setAttribute('aria-expanded', String(willOpen));
    button.textContent = willOpen ? 'Hide answer review' : 'Review all my answers';
  };

  buildAnswerReview() {
    const container = this.querySelector('[data-answer-review]');
    if (!container || container.childElementCount) return;

    const heading = document.createElement('h3');
    heading.textContent = 'Your answer breakdown';
    const list = document.createElement('ol');

    this.questions.forEach((question, index) => {
      const answer = this.state.answers[index];
      const selectedInput = question.querySelector(`[data-answer-input][value="${answer?.selected || ''}"]`);
      const correctInput = question.querySelector(
        `[data-answer-input][value="${question.dataset.correctAnswer || ''}"]`
      );
      const item = document.createElement('li');
      const title = document.createElement('strong');
      const result = document.createElement('span');
      const answerLine = document.createElement('p');
      const explanation = document.createElement('p');

      title.textContent = question.querySelector('[data-question-heading]')?.textContent.trim() || `Question ${index + 1}`;
      result.textContent = answer?.correct ? 'Correct' : 'Worth a refresher';
      result.className = answer?.correct ? 'is-correct' : 'is-incorrect';
      answerLine.textContent = answer?.correct
        ? `Your answer: ${selectedInput?.closest('[data-answer-card]')?.querySelector('.fiber-quiz__answer-text')?.textContent.trim()}`
        : `Correct answer: ${correctInput?.closest('[data-answer-card]')?.querySelector('.fiber-quiz__answer-text')?.textContent.trim()}`;
      explanation.textContent = question.dataset.explanation || '';

      item.append(title, result, answerLine, explanation);

      if (question.dataset.sourceUrl) {
        const source = document.createElement('a');
        source.href = question.dataset.sourceUrl;
        source.target = '_blank';
        source.rel = 'noopener';
        source.textContent = `Source: ${question.dataset.sourceLabel || 'Learn more'}`;
        item.append(source);
      }
      list.append(item);
    });

    container.append(heading, list);
  }

  handlePurchaseOptionChange = (event) => {
    this.querySelectorAll('.fiber-quiz__purchase-option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(event.target));
    });
  };

  handleAddTempo = async () => {
    const selected = this.querySelector('[data-purchase-option]:checked');
    const button = this.querySelector('[data-add-tempo]');
    const label = this.querySelector('[data-add-label]');
    const status = this.querySelector('[data-cart-status]');
    if (!(selected instanceof HTMLInputElement) || !button || !label) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    label.textContent = 'Adding…';
    if (status) status.textContent = '';

    const item = {
      id: Number(selected.dataset.variantId),
      quantity: 1,
    };
    if (selected.dataset.sellingPlanId) item.selling_plan = Number(selected.dataset.sellingPlanId);

    try {
      const addResponse = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });
      const addData = await addResponse.json();
      if (!addResponse.ok || addData.status) throw new Error(addData.description || 'Unable to add Tempo.');

      let discountApplied = false;
      if (this.dataset.discountCode) {
        const discountResponse = await fetch(this.dataset.cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ discount: this.dataset.discountCode }),
        });
        const discountData = await discountResponse.json();
        discountApplied = Boolean(
          discountData.discount_codes?.some(
            (discount) => discount.code === this.dataset.discountCode && discount.applicable !== false
          )
        );
      }

      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`);
      const cart = await cartResponse.json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'fiber-quiz',
            data: {
              source: 'fiber-quiz',
              productId: selected.dataset.variantId,
              itemCount: 1,
            },
          },
        })
      );

      label.textContent = 'Added';
      if (status) {
        if (this.dataset.discountCode) {
          status.textContent = discountApplied
            ? `${this.dataset.discountCode} is applied to your cart.`
            : `Tempo is in your cart. Use ${this.dataset.discountCode} at checkout.`;
        } else if (selected.value === 'subscription') {
          status.textContent = 'Tempo is in your cart with subscription savings.';
        } else {
          status.textContent = 'Tempo is in your cart.';
        }
      }
      this.track('tempo_quiz_product_added', {
        quiz_type: this.dataset.quizType,
        difficulty: this.state.difficulty,
        purchase_option: selected.value,
        discount_applied: discountApplied,
      });
      window.setTimeout(() => {
        label.textContent = 'Add Tempo to cart';
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
      }, 1800);
    } catch (error) {
      label.textContent = 'Try again';
      if (status) status.textContent = error.message || 'Unable to add Tempo. Please try again.';
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
    }
  };

  applySavedAnswers() {
    this.state.answers.forEach((answer, index) => {
      const question = this.questions[index];
      if (!question || !answer) return;

      const selected = question.querySelector(`[data-answer-input][value="${answer.selected}"]`);
      if (selected instanceof HTMLInputElement) selected.checked = true;
      if (answer.graded) {
        this.renderGradedQuestion(question, answer);
      } else {
        const checkButton = question.querySelector('[data-check-answer]');
        if (checkButton) checkButton.disabled = false;
      }
    });
  }

  saveState() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      // The quiz remains fully functional when storage is unavailable.
    }
  }

  restoreState() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
      if (!saved || !Array.isArray(saved.answers)) return;
      this.state = {
        started: Boolean(saved.started),
        currentQuestion: Number(saved.currentQuestion) || 0,
        answers: saved.answers,
        unlocked: Boolean(saved.unlocked),
        difficulty: ['beginner', 'medium', 'hard'].includes(saved.difficulty) ? saved.difficulty : 'beginner',
        pendingLead: Boolean(saved.pendingLead),
      };
    } catch (error) {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (storageError) {
        // Ignore browsers that block session storage entirely.
      }
    }
  }

  scrollExperienceIntoView() {
    const top = this.querySelector('.fiber-quiz__experience')?.getBoundingClientRect().top || 0;
    if (top < 0) {
      this.querySelector('.fiber-quiz__experience')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  setStatus(message) {
    const status = this.querySelector('[data-quiz-status]');
    if (status) status.textContent = message;
  }

  track(eventName, properties = {}) {
    window.dataLayer?.push({ event: eventName, ...properties });
    try {
      window.klaviyo?.track?.(eventName, properties);
    } catch (error) {
      // Analytics must never block quiz progress.
    }
  }
}

if (!customElements.get('tempo-fiber-quiz')) {
  customElements.define('tempo-fiber-quiz', TempoFiberQuiz);
}
